import React, { useState, useEffect } from 'react';
import { DmnModel } from '../../types';
import { Modal, Button, Input } from '../atoms/BaseComponents';
import { evaluateDmn } from '../../utils/dmnLogic';
import { Play, RotateCcw } from 'lucide-react';

interface TestModalProps {
  isOpen: boolean;
  onClose: () => void;
  model: DmnModel;
}

export const TestModal = ({ isOpen, onClose, model }: TestModalProps) => {
  const inputCols = model.columns.filter(c => c.type === 'input');
  const [inputs, setInputs] = useState<Record<string, string>>({});
  const [results, setResults] = useState<any[] | null>(null);

  // Reset state when modal opens
  useEffect(() => {
    if (isOpen) {
        setResults(null);
    }
  }, [isOpen]);

  const handleInputChange = (key: string, value: string) => {
    setInputs(prev => ({ ...prev, [key]: value }));
  };

  const runTest = () => {
    const evalResults = evaluateDmn(model, inputs);
    setResults(evalResults);
  };

  const reset = () => {
      setInputs({});
      setResults(null);
  }

  return (
    <Modal isOpen={isOpen} onClose={onClose} title={`Test ${model.name}`}>
      <div className="space-y-6">
        
        {/* Input Form */}
        <div className="space-y-3">
          {inputCols.length === 0 && <div className="text-slate-500 dark:text-slate-400 text-sm">No input columns defined.</div>}
          {inputCols.map(col => (
            <div key={col.id} className="grid grid-cols-3 gap-2 items-center">
              <label className="text-xs font-semibold text-slate-500 dark:text-slate-400 uppercase text-right col-span-1 truncate" title={col.label}>
                {col.label}
              </label>
              <Input 
                type={col.dataType === 'number' ? 'number' : 'text'}
                placeholder={`(${col.dataType})`}
                className="col-span-2 bg-slate-50 dark:bg-slate-800 border border-slate-200 dark:border-slate-700 rounded px-2 py-1.5 focus:ring-2 focus:ring-primary/20"
                value={inputs[col.variableName || col.label] || ''}
                onChange={(e) => handleInputChange(col.variableName || col.label, e.target.value)}
              />
            </div>
          ))}
        </div>

        {/* Action Bar */}
        <div className="flex items-center justify-end gap-2 pt-2 border-t border-slate-100 dark:border-slate-800">
            <Button variant="ghost" size="sm" onClick={reset}>
                <RotateCcw size={14} /> Reset
            </Button>
            <Button variant="primary" size="sm" onClick={runTest} disabled={inputCols.length === 0}>
                <Play size={14} /> Run Test
            </Button>
        </div>

        {/* Results Area */}
        {results && (
            <div className="animate-in slide-in-from-top-2 duration-300">
                <div className="flex items-center gap-2 mb-2">
                    <h4 className="text-xs font-bold text-slate-600 dark:text-slate-300 uppercase">Output Result</h4>
                    <span className="text-[10px] bg-slate-200 dark:bg-slate-700 text-slate-600 dark:text-slate-300 px-1.5 py-0.5 rounded-full">{results.length} matched rules</span>
                </div>
                
                {results.length === 0 ? (
                    <div className="p-4 bg-slate-50 dark:bg-slate-800 border border-slate-200 dark:border-slate-700 rounded text-sm text-slate-500 dark:text-slate-400 italic text-center">
                        No rules matched the current inputs.
                    </div>
                ) : (
                    <div className="space-y-2">
                        {results.map((res, idx) => (
                            <div key={idx} className="bg-emerald-50/50 dark:bg-emerald-900/20 p-3 rounded border border-emerald-100/50 dark:border-emerald-800/30 shadow-sm text-sm">
                                <div className="text-[10px] text-emerald-600/50 dark:text-emerald-400/50 font-mono mb-1">MATCH #{idx + 1}</div>
                                {Object.entries(res).map(([k, v]) => (
                                    <div key={k} className="flex justify-between items-center py-1 border-b border-emerald-100/30 dark:border-emerald-800/30 last:border-0">
                                        <span className="text-slate-500 dark:text-emerald-200/70 font-medium">{k}:</span>
                                        <span className="font-bold text-emerald-700 dark:text-emerald-400 font-mono">{String(v)}</span>
                                    </div>
                                ))}
                            </div>
                        ))}
                    </div>
                )}
            </div>
        )}
      </div>
    </Modal>
  );
};